import { z } from 'zod'
import { UserDtoSchema, UserRoleSchema, type UserDto } from '~/services/api/generated'
import { Normalization } from '~/utils/normalization'
import { parseVocabularyNames } from '~/utils/parseVocabularyNames'
import type { Logger } from '~/interfaces/Logger'

const isRecord = Normalization.isRecord

const IdentityWireSchema = UserDtoSchema.extend({
  roles: z.array(z.string())
})

const parseRoles = (names: readonly string[], logger: Logger): UserDto['roles'] =>
  parseVocabularyNames(UserRoleSchema, names, logger, 'roleOutsideVocabulary')

const toUserResource = (wire: z.output<typeof IdentityWireSchema>, logger: Logger): UserDto => {
  return { ...wire, roles: parseRoles(wire.roles, logger) }
}

/**
 * Validates an unknown value against the canonical `UserDto` shape,
 * accepting either the bare resource or the Laravel `{ data }` envelope.
 * Role names outside the UserRole vocabulary are logged and dropped.
 * Returns null on anything that fails validation -- never a partial object.
 */
export function normalizeIdentity(value: unknown, logger: Logger): UserDto | null {
  const direct = IdentityWireSchema.safeParse(value)
  if (direct.success) return toUserResource(direct.data, logger)
  if (!isRecord(value)) return null

  const wrapped = IdentityWireSchema.safeParse(value['data'])
  return wrapped.success ? toUserResource(wrapped.data, logger) : null
}
